
import { ReactNode, useState, useEffect } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, Shield, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import DashboardBreadcrumb from "@/components/DashboardBreadcrumb";
import { useContentManager } from "@/hooks/useContentManager";
import { ContentLoader } from "@/components/ContentLoader";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { usePageData } from "@/hooks/usePageData";
import { DashboardSidebar } from "@/components/dashboard/sidebar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"; 
import { useIsMobile } from "@/hooks/use-mobile";

interface DashboardLayoutProps {
  children?: ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const isMobile = useIsMobile();
  
  const location = useLocation();
  const { preloadView } = useContentManager();
  const { isLoading: pageLoading, error: pageError } = usePageData(location.pathname);
  
  // Get current section from URL
  const currentSection = location.pathname.split('/').pop() || 'dashboard';
  const sectionTitle = currentSection.charAt(0).toUpperCase() + currentSection.slice(1);
  
  // Preload users page when hovering over the users menu item
  const handleUserMenuHover = () => {
    preloadView("/dashboard/users");
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <DashboardSidebar isOpen={sidebarOpen} userRole={user?.role} />

      <div className={cn(
        "flex-1 flex flex-col transition-all duration-300 ease-in-out overflow-auto",
        sidebarOpen ? "md:ml-64" : "md:ml-20"
      )}>
        {/* Header */}
        <header className="bg-white border-b border-slate-200 p-4 sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={toggleSidebar}
                    className="hidden md:flex"
                  >
                    <Menu size={20} />
                  </Button>
                  <h1 className="text-xl font-semibold text-slate-900">{sectionTitle}</h1>
                </div>
                
                <div className="flex items-center gap-3">
                  {isMobile ? (
                    <Sheet>
                      <SheetTrigger asChild>
                        <Button variant="outline" size="sm" className="flex items-center gap-1">
                          <Shield size={16} />
                          <span className="hidden sm:inline">{user?.role || 'User'}</span>
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="right" className="w-[280px] sm:w-[400px]">
                        <div className="px-1 py-8 h-full flex flex-col">
                          <h2 className="text-lg font-semibold mb-6">Account</h2>
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm text-slate-500">Signed in as</p>
                              <p className="font-medium">{user?.email}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-500">Role</p>
                              <p className="font-medium capitalize">{user?.role || 'User'}</p>
                            </div>
                          </div>
                          <div className="mt-auto">
                            <Button 
                              variant="destructive" 
                              className="w-full" 
                              onClick={handleSignOut}
                            >
                              Sign Out
                            </Button>
                          </div>
                        </div>
                      </SheetContent>
                    </Sheet>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="hidden sm:flex items-center gap-1"
                        onMouseEnter={handleUserMenuHover}
                      >
                        <Shield size={16} />
                        <span>{user?.role || 'User'}</span>
                      </Button>
                      <Button 
                        onClick={handleSignOut} 
                        variant="outline" 
                        size="sm"
                        className="flex items-center gap-1"
                      >
                        <LogOut size={16} />
                        <span className="hidden sm:inline">Sign Out</span>
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <DashboardBreadcrumb />
            </div>
          </div>
        </header>

        {/* Content with loading state and error boundary */}
        <main className="flex-1 p-4 sm:p-6 max-w-7xl mx-auto w-full">
          <ErrorBoundary>
            <ContentLoader isLoading={pageLoading} error={pageError}>
              {children || <Outlet />}
            </ContentLoader>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
